
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Coins, MapPin, Phone, Truck, Star, Apple } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";

const AchatMetauxTarnEtGaronne = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title="Achat Métaux Tarn-et-Garonne (82) - Montauban | Groupe Fabre"
        description="Rachat de métaux et ferrailles en Tarn-et-Garonne : Montauban, Castelsarrasin, Moissac. Solutions agricoles, prix attractifs."
        canonical="/nos-services/achat-metaux/tarn-et-garonne"
        keywords="achat métaux Tarn-et-Garonne, rachat ferraille Montauban, achat cuivre Moissac, ferrailleur 82"
        localBusiness={{ city: "Montauban", region: "Tarn-et-Garonne", postalCode: "82000", serviceType: "Achat de métaux" }}
      />
      <Navbar />
      
      <main className="flex-1 mt-16">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <div className="flex items-center mb-4">
                <Apple className="h-12 w-12 mr-4" />
                <div>
                  <h1 className="text-4xl md:text-5xl font-bold">Achat Métaux Tarn-et-Garonne (82)</h1>
                  <p className="text-xl mt-2 opacity-90">Vergers et industrie - Solutions agricoles</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold text-fabre-gray mb-6">
                  Rachat de métaux en Tarn-et-Garonne
                </h2>
                <p className="text-gray-600 mb-6">
                  Le <strong>Tarn-et-Garonne</strong> conjugue agriculture intensive et industrie. 
                  <strong>Groupe Fabre Environnement</strong> propose des solutions adaptées 
                  aux exploitations agricoles et aux entreprises industrielles de ce département 
                  dynamique.
                </p>
                
                <h3 className="text-xl font-bold text-fabre-gray mb-4">Secteurs d'intervention</h3>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-purple-500 text-white mr-3">•</span>
                    <span><strong>Montauban :</strong> Préfecture et industries</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-purple-500 text-white mr-3">•</span>
                    <span><strong>Castelsarrasin :</strong> Zone industrielle</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-purple-500 text-white mr-3">•</span>
                    <span><strong>Moissac :</strong> Agriculture et patrimoine</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-purple-500 text-white mr-3">•</span>
                    <span><strong>Caussade :</strong> Secteur agricole</span>
                  </li>
                </ul>

                <div className="bg-purple-50 border border-purple-200 p-6 rounded-lg">
                  <h4 className="font-bold text-purple-800 mb-3">🍎 Spécialisation agricole</h4>
                  <ul className="text-purple-700 text-sm space-y-2">
                    <li>• Expertise matériel agricole et arboriculture</li>
                    <li>• Solutions pour exploitations intensives</li>
                    <li>• Service adapté aux cycles agricoles</li>
                    <li>• Partenariats coopératives locales</li>
                  </ul>
                </div>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-fabre-gray mb-6">Service Tarn-et-Garonne</h3>
                <p className="text-gray-600 mb-6">
                  Notre service s'adapte aux rythmes agricoles et industriels du département 
                  pour vous offrir la meilleure valorisation.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center">
                    <Apple className="h-5 w-5 text-purple-500 mr-3" />
                    <span>Solutions agro-industrielles</span>
                  </div>
                  <div className="flex items-center">
                    <Coins className="h-5 w-5 text-purple-500 mr-3" />
                    <span>Prix agriculteurs</span>
                  </div>
                  <div className="flex items-center">
                    <Star className="h-5 w-5 text-purple-500 mr-3" />
                    <span>Service flexible</span>
                  </div>
                </div>

                <Link to="/contact" className="block">
                  <button className="w-full px-6 py-3 bg-purple-500 text-white rounded-md font-medium hover:bg-purple-600 transition-colors">
                    Demander prix 82
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Spécificités 82 */}
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-fabre-gray mb-8 text-center">
              Spécialisations Tarn-et-Garonne
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Arboriculture</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Matériel de vergers, équipements fruitiers
                </p>
                <div className="text-green-600 font-medium">Tarifs arboriculteurs</div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Industrie Agro</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Équipements transformation, silos
                </p>
                <div className="text-orange-600 font-medium">Prix industriels</div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Logistique</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Transport, stockage, manutention
                </p>
                <div className="text-blue-600 font-medium">Service logistique</div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AchatMetauxTarnEtGaronne;
